import is from '@sindresorhus/is';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import type { CommentClassification } from './comment-classification';
import type { CommentsScope } from './comments-scope-context';
import type { DetailsSnapshot } from './item-details-cache';
import { scrollAnchorIntoViewportIfNeeded, scrollOffsetForAncestor } from './scroll-anchor-into-view';
import type { CommentAnchor, CommentTarget } from './types';

export type PendingScope = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly version: number;
};

export type PendingCommentTarget = {
    readonly target: CommentTarget;
    readonly anchorPageY: number;
    readonly anchorPageBottom: number;
    readonly anchor: CommentAnchor | null;
    readonly commentId: string;
    readonly prevDetails: DetailsSnapshot | null;
    readonly scope: PendingScope | null;
};

export type ActiveThreadState = {
    readonly activeThreadId: string | null;
    readonly commentClassification: CommentClassification | null;
    readonly commentClassificationScope: CommentsScope | null;
    readonly pendingTarget: PendingCommentTarget | null;
    readonly isCommentsOpen: boolean;
    readonly viewMode: CommentsViewMode;
    readonly isRailOpening: boolean;
};

export type CommentsViewMode = 'active' | 'hidden';

export type SetPendingTargetParams = {
    readonly target: CommentTarget;
    readonly anchorRect: DOMRect;
    readonly anchorEl: Element | null;
    readonly anchor?: CommentAnchor | null;
    readonly commentId: string;
    readonly prevDetails: DetailsSnapshot | null;
    readonly scope?: PendingScope;
};

export type RestoreDetails = (snapshot: DetailsSnapshot) => void;
export type InvalidateDetails = (scope: PendingScope) => void;

export type ActiveThreadActions = {
    readonly setActiveThreadId: (id: string | null) => void;
    readonly setCommentClassification: (classification: CommentClassification | null, scope?: CommentsScope) => void;
    readonly setPendingTarget: (params: SetPendingTargetParams) => void;
    readonly cancelPending: () => void;
    readonly clearPending: () => void;
    readonly setIsCommentsOpen: (open: boolean) => void;
    readonly toggleCommentsOpen: () => void;
    readonly setViewMode: (mode: CommentsViewMode) => void;
    readonly setRailOpening: (opening: boolean) => void;
    readonly setRestoreDetails: (restore: RestoreDetails | null) => void;
    readonly setInvalidateDetails: (invalidate: InvalidateDetails | null) => void;
};

export type ActiveThreadStore = ActiveThreadActions & ActiveThreadState;

type ActiveThreadStoreInitialState = {
    readonly isCommentsOpen?: boolean;
};

export const createActiveThreadStore = (
    initialState: ActiveThreadStoreInitialState = {},
): StoreApi<ActiveThreadStore> =>
    createStore<ActiveThreadStore>((set, get) => {
        let restoreDetails: RestoreDetails | null = null;
        let invalidateDetails: InvalidateDetails | null = null;
        return {
            activeThreadId: null,
            commentClassification: null,
            commentClassificationScope: null,
            pendingTarget: null,
            isCommentsOpen: initialState.isCommentsOpen ?? false,
            viewMode: 'active',
            isRailOpening: false,
            setActiveThreadId: (id) => set({ activeThreadId: id }),
            setCommentClassification: (classification, scope) =>
                set({
                    commentClassification: classification,
                    commentClassificationScope: is.null(classification) ? null : (scope ?? null),
                }),
            setIsCommentsOpen: (open) => set({ isCommentsOpen: open, viewMode: open ? 'active' : get().viewMode }),
            toggleCommentsOpen: () =>
                set((state) => ({
                    isCommentsOpen: !state.isCommentsOpen,
                    viewMode: state.isCommentsOpen ? state.viewMode : 'active',
                })),
            setViewMode: (mode) => {
                if (mode === 'hidden') {
                    get().cancelPending();
                    set({ activeThreadId: null, viewMode: mode });
                    return;
                }
                set({ viewMode: mode });
            },
            setRailOpening: (opening) => set({ isRailOpening: opening }),
            setRestoreDetails: (restore) => {
                restoreDetails = restore;
            },
            setInvalidateDetails: (invalidate) => {
                invalidateDetails = invalidate;
            },
            setPendingTarget: ({ target, anchorRect, anchorEl, anchor, commentId, prevDetails, scope }) => {
                const scrollOffset = scrollOffsetForAncestor(anchorEl);
                set({
                    activeThreadId: null,
                    isCommentsOpen: true,
                    pendingTarget: {
                        target,
                        anchorPageY: anchorRect.top + scrollOffset,
                        anchorPageBottom: anchorRect.bottom + scrollOffset,
                        anchor: anchor ?? null,
                        commentId,
                        prevDetails,
                        scope: scope ?? null,
                    },
                    viewMode: 'active',
                });
                scrollAnchorIntoViewportIfNeeded(anchorEl, anchorRect);
            },
            cancelPending: () => {
                const pending = get().pendingTarget;
                if (!is.null(pending)) {
                    if (!is.null(pending.prevDetails) && !is.null(restoreDetails)) {
                        restoreDetails(pending.prevDetails);
                    }
                    if (!is.null(pending.scope) && !is.null(invalidateDetails)) {
                        invalidateDetails(pending.scope);
                    }
                }
                set({ pendingTarget: null });
            },
            clearPending: () => set({ pendingTarget: null }),
        };
    });
